import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const http = axios.create({ baseURL: API_BASE, timeout: 120000 });

/* --- wedding access token -------------------------------------------------
   One token per wedding, kept in localStorage so a reload does not ask for
   the password again. Attached from the wedding id in the request URL; the
   job endpoint has no id in its path, hence the remembered active wedding. */
let activeWedding = null;
const tokenKey = (id) => `wt-token-${id}`;

export const setActiveWedding = (id) => {
  activeWedding = id;
};
export const getToken = (id) => (id ? localStorage.getItem(tokenKey(id)) || "" : "");
export const setToken = (id, token) => localStorage.setItem(tokenKey(id), token);
export const clearToken = (id) => localStorage.removeItem(tokenKey(id));

http.interceptors.request.use((config) => {
  // A call that brought its own key keeps it: the studio signs its requests
  // with the site password, and the remembered wedding token below would
  // otherwise replace it as soon as someone had opened a wedding in this tab.
  if (config.headers?.Authorization) return config;
  const match = (config.url || "").match(/\/weddings\/([^/?]+)/);
  const token = getToken(match ? match[1] : activeWedding);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const isUnauthorized = (error) => error?.response?.status === 401;
export const login = (id, password) =>
  unwrap(http.post(`/weddings/${id}/auth`, { password }));
export const weddingIsProtected = (id) => unwrap(http.get(`/weddings/${id}/protected`));
export const changePassword = (id, currentPassword, newPassword) =>
  unwrap(
    http.post(`/weddings/${id}/password`, {
      current_password: currentPassword,
      new_password: newPassword,
    })
  );

const unwrap = (promise) => promise.then((res) => res.data);

export const apiHealth = () => unwrap(http.get("/health"));
export const createDemoWedding = () => unwrap(http.post("/weddings/demo"));
export const createWedding = (payload) => unwrap(http.post("/weddings", payload));
export const getWedding = (id) => unwrap(http.get(`/weddings/${id}`));
export const getWeddingPublic = (id) => unwrap(http.get(`/weddings/${id}/public`));
export const patchWedding = (id, payload) => unwrap(http.patch(`/weddings/${id}`, payload));

export const listGuests = (id) => unwrap(http.get(`/weddings/${id}/guests`));
export const addGuest = (id, payload) => unwrap(http.post(`/weddings/${id}/guests`, payload));
export const editGuest = (id, guestId, payload) =>
  unwrap(http.put(`/weddings/${id}/guests/${guestId}`, payload));
export const deleteGuest = (id, guestId) => unwrap(http.delete(`/weddings/${id}/guests/${guestId}`));
export const submitRsvp = (id, payload) => unwrap(http.post(`/weddings/${id}/rsvp`, payload));
/** What one guest's own invitation link opens. Public: the link is the key. */
export const getRsvpPrefill = (id, guestToken) =>
  unwrap(http.get(`/weddings/${id}/rsvp/${guestToken}`));
export const getInvitations = (id, rsvpBase) =>
  unwrap(http.get(`/weddings/${id}/invitations`, { params: { rsvp_base: rsvpBase } }));

export const startGeneration = (id) => unwrap(http.post(`/weddings/${id}/generate`));
export const getJob = (jobId) => unwrap(http.get(`/jobs/${jobId}`));
export const getPlan = (id) => unwrap(http.get(`/weddings/${id}/plan`));
export const moveGuest = (id, payload) => unwrap(http.post(`/weddings/${id}/plan/move`, payload));
export const lockTable = (id, table, locked) =>
  unwrap(http.post(`/weddings/${id}/plan/lock`, { table, locked }));
export const pinGuest = (id, guestId, pinned) =>
  unwrap(http.post(`/weddings/${id}/plan/pin`, { guest_id: guestId, pinned }));
export const refreshNotes = (id) => unwrap(http.post(`/weddings/${id}/plan/refresh-notes`));
const withToken = (url, id) => {
  const token = getToken(id);
  if (!token) return url;
  return `${url}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
};
export const planPdfUrl = (id, download = false) =>
  withToken(`${API_BASE}/weddings/${id}/plan/pdf${download ? "?download=1" : ""}`, id);

export const errorMessage = (error, fallback = "Something went wrong") =>
  error?.response?.data?.detail || error?.message || fallback;

export const consentText = () => unwrap(http.get("/consent-text"));
export const guestTemplateUrl = () => `${API_BASE}/guests/template.xlsx`;
export const guestExportUrl = (id) => withToken(`${API_BASE}/weddings/${id}/guests/export.xlsx`, id);
export const importGuests = (id, file) => {
  const data = new FormData();
  data.append("file", file);
  return unwrap(
    http.post(`/weddings/${id}/guests/import`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    })
  );
};
export const getInvitation = (id, rsvpUrl) =>
  unwrap(http.get(`/weddings/${id}/invitation`, { params: { rsvp_url: rsvpUrl } }));
export const renameTable = (id, table, title) =>
  unwrap(http.post(`/weddings/${id}/plan/rename`, { table, title }));
export const bulkDeleteGuests = (id, guestIds) =>
  unwrap(http.post(`/weddings/${id}/guests/bulk-delete`, { guest_ids: guestIds }));
export const bulkUpdateGuests = (id, guestIds, changes) =>
  unwrap(http.post(`/weddings/${id}/guests/bulk-update`, { guest_ids: guestIds, ...changes }));
export const suggestMoves = (id, guestIds) =>
  unwrap(http.post(`/weddings/${id}/plan/suggest`, { guest_ids: guestIds }));

/* --- sending invitations from Placy ------------------------------------------
   The server sends in the couple's name and remembers who got what; the
   reminders run on their own afterwards. */
export const sendInvitations = (id, guestIds = null) =>
  unwrap(http.post(`/weddings/${id}/invitations/send`, { guest_ids: guestIds }, { timeout: 180000 }));
export const previewInvitation = (id) =>
  unwrap(http.post(`/weddings/${id}/invitations/preview`, {}, { timeout: 60000 }));
